import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import { PaymentMode } from '@molecules/PaymentModes/PaymentModes';
import { trackAddPaymentInfo } from '@helpers/analyticsHelpers/trackCheckoutStep';
import InvoiceButton from '@molecules/CheckoutPaymentButton/buttons/InvoiceButton';
import Text from '@atoms/Text/Text';
import Modal from '@organisms/Modal/Modal';
import paths from '@constants/paths';
import getCheckoutPaymentErrorMessage from '@helpers/checkoutPaymentErrorMessage';
import Link from '@atoms/Link/Link';
import useCustomer from '@hooks/useCustomer';
import Heading from '@atoms/Heading/Heading';
import { selectCheckoutPayment, setEditing, setPossiblyWrongStoreConfirmed } from '@slices/checkoutSlice';
import {
  trackCheckoutSessionTimeout,
  trackCheckoutSubmitError,
} from '@helpers/analyticsHelpers/trackCheckoutSubmitError';
import { PlaceOrderErrorResponse } from '@enums/PlaceOrderErrorResponse';
import PayExButton from './buttons/PayExButton';
import KlarnaButton from './buttons/KlarnaButton';
import {
  StyledCheckoutPaymentButtonWrapper,
  StyledCheckoutSubmitButton,
  StyledErrorModalContent,
  StyledSupportText,
} from './CheckoutPaymentButton.styles';
import useCart from '@hooks/useCart';

export enum PossiblyWrongStoreConfirmedState {
  NONE, // Initial state, no confirmation required
  UNCONFIRMED, // Received wrong store error, prompt user to confirm store
  CONFIRMED_IN_MODAL, // Confirmed in wrong store modal, order should be auto submitted
  CONFIRMED_IN_DELIVERY, // Confirmed in delivery section, requires manually re-submitting the order
}

interface Props {
  errorData: ErrorDataType;
  setErrorData: (error: ErrorDataType) => void;
}

const CheckoutPaymentButton = ({ errorData, setErrorData }: Props) => {
  const payment = useAppSelector(selectCheckoutPayment);
  const { cart, hasFetchedCart } = useCart();
  const { customer } = useCustomer();
  const dispatch = useAppDispatch();
  const t = useTranslations('checkoutpage');

  const isWrongStoreError = useMemo(
    () => errorData.errorCode === PlaceOrderErrorResponse.WRONG_STORE,
    [errorData.errorCode]
  );

  const { saveCard, selectedCard, possiblyWrongStoreConfirmed, isVoucherSubmitting } = payment;

  const onCloseHandler = () => {
    if (isWrongStoreError) {
      dispatch(setEditing({ boxName: 'delivery', isEditing: true }));
      document.getElementById('checkout-delivery-section')?.scrollIntoView({ behavior: 'smooth' });
      dispatch(setPossiblyWrongStoreConfirmed(PossiblyWrongStoreConfirmedState.UNCONFIRMED));
    }

    clearError();
  };

  const clearError = () => {
    setErrorData({ ...errorData, errorCode: '', show: false });
  };

  const onConfirmHandler = () => {
    if (errorData.errorCode === PlaceOrderErrorResponse.WRONG_STORE) {
      dispatch(setPossiblyWrongStoreConfirmed(PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL));
    } else if (errorData.errorCode === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    clearError();
  };

  const [prevPayment, setPrevPayment] = useState<typeof payment | undefined>();

  useEffect(() => {
    if (
      hasFetchedCart &&
      !payment.isDisabled &&
      (payment.selectedMode !== prevPayment?.selectedMode ||
        payment?.selectedCard?.id !== prevPayment?.selectedCard?.id)
    ) {
      cart && trackAddPaymentInfo(cart, payment, undefined, !customer?.isAnonymous);
      setPrevPayment(payment);
    }
  }, [cart, hasFetchedCart, payment, prevPayment]);

  useEffect(() => {
    if (errorData.errorCode) {
      const error = getCheckoutPaymentErrorMessage(errorData);
      setErrorData(error);
      if (error.show) {
        if (errorData.errorCode === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
          trackCheckoutSessionTimeout(t(error.text));
        } else {
          trackCheckoutSubmitError(t(error.text), errorData.errorCode);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorData.errorCode]);

  const buttonContent = t('button.pay');
  const checkoutError = errorData.errorCode ? getCheckoutPaymentErrorMessage(errorData) : null;

  const isButtonDisabled = payment.isDisabled || isVoucherSubmitting;

  const getPaymentButton = () => {
    switch (payment.selectedMode) {
      case PaymentMode.INVOICE:
        return (
          <InvoiceButton
            disabled={isButtonDisabled}
            setError={setErrorData}
            possiblyWrongStoreConfirmed={possiblyWrongStoreConfirmed}
          >
            {buttonContent}
          </InvoiceButton>
        );
      case PaymentMode.PAYEX:
      case PaymentMode.PSPPAYEXALL:
      case PaymentMode.PAYEXALL:
      case PaymentMode.SWEDBANKPAY:
        return (
          <PayExButton
            disabled={isButtonDisabled}
            saveCard={saveCard}
            selectedCard={selectedCard}
            setError={setErrorData}
            possiblyWrongStoreConfirmed={possiblyWrongStoreConfirmed}
          >
            {buttonContent}
          </PayExButton>
        );
      case PaymentMode.KLARNA:
        return (
          <KlarnaButton
            disabled={isButtonDisabled}
            setError={setErrorData}
            possiblyWrongStoreConfirmed={possiblyWrongStoreConfirmed}
          >
            {buttonContent}
          </KlarnaButton>
        );
      default:
        return <StyledCheckoutSubmitButton disabled>{buttonContent}</StyledCheckoutSubmitButton>;
    }
  };

  return (
    <StyledCheckoutPaymentButtonWrapper data-testid="checkout-payment-button">
      {getPaymentButton()}
      {errorData.show && checkoutError && (
        <Modal
          size="medium"
          onClose={onCloseHandler}
          confirmButtonText={isWrongStoreError ? t('button.confirmChoice') : t('button.confirm')}
          onConfirm={onConfirmHandler}
          cancelButtonText={isWrongStoreError ? t('button.edit') : ''}
          closeOnEscape
          buttonWidth={!isWrongStoreError ? 100 : undefined}
          mobileButtonWidth={!isWrongStoreError ? 100 : undefined}
          equalButtonWidth={isWrongStoreError}
          verticallyCentered
          hasCloseIcon={errorData.errorCode !== PlaceOrderErrorResponse.NO_DELIVERY_SLOT}
        >
          <StyledErrorModalContent>
            <Heading variant="h3">{t(checkoutError.title)}</Heading>
            <Text type="body">
              {checkoutError.text === 'error.orderLimit.text'
                ? t(checkoutError.text, { orderLimit: customer?.creditInformation?.orderLimit ?? '' })
                : t(checkoutError.text)}
            </Text>

            {isWrongStoreError && (
              <div>
                <Text type="body">
                  <strong>{t('defaultAddress.name')}</strong>
                </Text>
                <Text type="body">{t('defaultAddress.line1')}</Text>
                <Text type="body">{t('defaultAddress.postalAndTown')}</Text>
              </div>
            )}

            {errorData.errorCode !== PlaceOrderErrorResponse.WRONG_STORE &&
              errorData.errorCode !== PlaceOrderErrorResponse.NO_DELIVERY_SLOT && (
                <StyledSupportText type="body">
                  {`${t('error.modal.supportText')} `}
                  <Link href={paths.external.ECOMMERCE_CONTACT}>{t('error.modal.supportLinkText')}</Link>
                </StyledSupportText>
              )}
          </StyledErrorModalContent>
        </Modal>
      )}
    </StyledCheckoutPaymentButtonWrapper>
  );
};

export default CheckoutPaymentButton;
